import { Box, Button, Container, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Check } from "lucide-react";
import type { CSSProperties, JSX, ReactNode } from "react";
import { Link as RouterLink } from "react-router-dom";

import type { Cta } from "./content.js";

/**
 * The standard visually-hidden recipe, applied through `style` rather than
 * `sx` on purpose — twice over.
 *
 * 1. MUI's `sx` treats a UNITLESS number as a theme/`%` value, so the
 *    obvious `sx={{ width: 1, height: 1 }}` compiles to `width: 100%;
 *    height: 100%` — a full-size box that only *looks* hidden because of
 *    `clip` and the layout's `overflowX: hidden`. It still takes part in
 *    layout and can push a grid track wide. `style` is raw CSS, so "1px"
 *    means one pixel.
 * 2. MUI's own `visuallyHidden` (`@mui/utils`) is not resolvable from this
 *    app: pnpm links only `@mui/material` and `@mui/icons-material` into
 *    `node_modules/@mui`, so importing it would mean adding a dependency
 *    the spec's "no new dependencies" rule forbids.
 */
const VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/** The building blocks the public pages are composed from. Each is a
 * semantic element with theme-token styling and nothing page-specific.
 *
 * There is deliberately no "eyebrow" — the small-caps label that used to
 * sit above every heading. The product owner's 2026-09-08 review: "remove
 * all these sub titles above topics." A heading that needs a label above it
 * to be understood is a heading that needs rewriting, and every one of them
 * here was a shorter restatement of the heading underneath. The space they
 * occupied is given back as padding rather than left as a gap. */

/** A page-level section: a `<section>` labelled by its own heading, with
 * consistent vertical rhythm and the site's max width. */
export function Section({ id, children, tone = "plain" }: { id: string; children: ReactNode; tone?: "plain" | "soft" }): JSX.Element {
  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={`${id}-heading`}
      sx={{
        py: { xs: 7, md: 10 },
        ...(tone === "soft" ? { bgcolor: (t) => alpha(t.palette.text.primary, 0.03) } : {}),
      }}
    >
      <Container maxWidth="lg">{children}</Container>
    </Box>
  );
}

export function SectionHeading({ id, title, lede }: { id: string; title: string; lede?: string }): JSX.Element {
  return (
    <Box sx={{ maxWidth: 720, mb: { xs: 4, md: 6 } }}>
      <Typography id={`${id}-heading`} component="h2" variant="h3" sx={{ fontWeight: 700, letterSpacing: "-0.02em", fontSize: { xs: "1.8rem", md: "2.4rem" } }}>
        {title}
      </Typography>
      {lede ? <Typography sx={{ mt: 2, fontSize: 17, color: "text.secondary", lineHeight: 1.6 }}>{lede}</Typography> : null}
    </Box>
  );
}

/** A responsive grid of cards: a title, an optional body paragraph, and an
 * optional bulleted list under it.
 *
 * The list is what lets the program page's "what each side commits to"
 * cards be this component rather than a fourth bespoke grid — a commitment
 * card is a heading over four promises, with no prose in between, so `body`
 * is optional too. */
export function CardGrid({ items, columns = 3, headingLevel = "h3" }: { items: readonly { title: string; body?: string; items?: readonly string[] }[]; columns?: 2 | 3 | 4; headingLevel?: "h3" | "h4" }): JSX.Element {
  return (
    <Box
      component="ul"
      sx={{
        listStyle: "none",
        p: 0,
        m: 0,
        display: "grid",
        gap: 3,
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", md: `repeat(${columns}, minmax(0,1fr))` },
      }}
    >
      {items.map((it) => (
        <Box
          component="li"
          key={it.title}
          sx={{
            p: 3,
            borderRadius: 3,
            border: 1,
            borderColor: "divider",
            bgcolor: (t) => alpha(t.palette.background.paper, t.palette.mode === "dark" ? 0.5 : 0.8),
            backdropFilter: "blur(8px)",
          }}
        >
          <Typography component={headingLevel} sx={{ fontSize: 17, fontWeight: 700, mb: 1 }}>{it.title}</Typography>
          {it.body ? <Typography sx={{ color: "text.secondary", lineHeight: 1.6 }}>{it.body}</Typography> : null}
          {it.items ? (
            <Box component="ul" sx={{ m: 0, mt: it.body ? 1.5 : 0, pl: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
              {it.items.map((line) => (
                <Typography component="li" key={line} sx={{ color: "text.secondary", lineHeight: 1.6, "&::marker": { color: "accent.500" } }}>
                  {line}
                </Typography>
              ))}
            </Box>
          ) : null}
        </Box>
      ))}
    </Box>
  );
}

/** Numbered steps, as an ordered list. */
export function StepList({ steps, "aria-label": ariaLabel }: { steps: readonly { title: string; body: string }[]; "aria-label"?: string }): JSX.Element {
  return (
    <Box component="ol" aria-label={ariaLabel} sx={{ listStyle: "none", p: 0, m: 0, display: "grid", gap: 3, gridTemplateColumns: { xs: "1fr", md: "repeat(4, minmax(0,1fr))" } }}>
      {steps.map((s, i) => (
        <Box component="li" key={s.title} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box aria-hidden sx={{ width: 40, height: 40, borderRadius: "50%", display: "grid", placeItems: "center", fontWeight: 700, color: "accent.contrastText", backgroundImage: (t) => `linear-gradient(135deg, ${t.palette.accent[600]}, ${t.palette.accent[800]})` }}>
            {i + 1}
          </Box>
          <Typography component="h3" sx={{ fontSize: 18, fontWeight: 700 }}>
            <Box component="span" style={VISUALLY_HIDDEN}>{`Step ${i + 1}: `}</Box>
            {s.title}
          </Typography>
          <Typography sx={{ color: "text.secondary", lineHeight: 1.6 }}>{s.body}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** A list of plain statements, each marked with a check.
 *
 * Lifted out of the (now removed) About page, where it rendered the
 * company's values: the same shape reads as "here is what applies to you"
 * on the program page's eligibility list, and it is the one list style on
 * the site that is neither a card grid nor numbered. The tick is decorative
 * — the `<ul>` already says "list" — so it is `aria-hidden`. */
export function CheckList({ items }: { items: readonly string[] }): JSX.Element {
  return (
    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 2, maxWidth: 900 }}>
      {items.map((item) => (
        <Box component="li" key={item} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box aria-hidden sx={{ display: "flex", flexShrink: 0, mt: 0.4, color: "accent.500" }}>
            <Check size={20} />
          </Box>
          <Typography sx={{ fontSize: 17, color: "text.primary", lineHeight: 1.6 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export interface TimelineItem {
  /** Whose move this is — rendered as a small pill above the title. */
  who: string;
  title: string;
  body: string;
  /** Rendered on its own muted line after a "You receive:" prefix. */
  receive: string;
}

/** A vertical timeline: a numbered dot per item on a left rail, with the
 * owner, the title, what happens, and what the reader ends up with.
 *
 * `StepList` is the horizontal, four-across version and stays the home
 * page's; this one is for a sequence that alternates between two actors and
 * carries a paragraph each, which four equal columns cannot hold at a
 * readable measure.
 *
 * The dot is `aria-hidden` — a decorative badge — so, exactly as in
 * `StepList`, the position is restored to the accessibility tree by a
 * visually-hidden "Step N: " inside the heading. The rail segment between
 * dots is absolutely positioned rather than a border on the `<li>`, so it
 * stops at the last item instead of trailing past it. */
export function Timeline({ items, "aria-label": ariaLabel }: { items: readonly TimelineItem[]; "aria-label"?: string }): JSX.Element {
  return (
    <Box component="ol" aria-label={ariaLabel} sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column" }}>
      {items.map((it, i) => {
        const last = i === items.length - 1;
        return (
          <Box component="li" key={it.title} sx={{ position: "relative", pl: { xs: 6, md: 7 }, pb: last ? 0 : { xs: 4, md: 5 } }}>
            {last ? null : (
              <Box aria-hidden sx={{ position: "absolute", left: 19, top: 44, bottom: 0, width: "2px", bgcolor: "divider" }} />
            )}
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                width: 40,
                height: 40,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                fontWeight: 700,
                color: "accent.contrastText",
                backgroundImage: (t) => `linear-gradient(135deg, ${t.palette.accent[600]}, ${t.palette.accent[800]})`,
              }}
            >
              {i + 1}
            </Box>
            <Typography
              component="p"
              sx={{
                display: "inline-block",
                mb: 1,
                px: 1.25,
                py: 0.25,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                color: "accent.500",
                border: 1,
                borderColor: (t) => alpha(t.palette.accent[500], 0.4),
                bgcolor: (t) => alpha(t.palette.accent[500], 0.08),
              }}
            >
              {it.who}
            </Typography>
            <Typography component="h3" sx={{ fontSize: 18, fontWeight: 700 }}>
              <Box component="span" style={VISUALLY_HIDDEN}>{`Step ${i + 1}: `}</Box>
              {it.title}
            </Typography>
            <Typography sx={{ mt: 1, color: "text.secondary", lineHeight: 1.6, maxWidth: 720 }}>{it.body}</Typography>
            <Typography sx={{ mt: 1.5, fontSize: 14, color: "text.secondary", maxWidth: 720 }}>
              <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>You receive:</Box>{" "}
              {it.receive}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

/** A primary CTA button (accent gradient pill) and an optional outlined one. */
export function CtaButton({ cta, variant = "primary", external = false }: { cta: Cta; variant?: "primary" | "secondary"; external?: boolean }): JSX.Element {
  const primary = variant === "primary";
  const linkProps = external ? { href: cta.href } : { component: RouterLink, to: cta.href };
  return (
    <Button
      {...linkProps}
      size="large"
      disableElevation
      variant={primary ? "contained" : "outlined"}
      sx={{
        borderRadius: 999,
        px: 3.5,
        py: 1.25,
        fontWeight: 700,
        fontSize: 16,
        textTransform: "none",
        ...(primary
          ? { color: "accent.contrastText", backgroundImage: (t) => `linear-gradient(135deg, ${t.palette.accent[600]}, ${t.palette.accent[800]})` }
          : { color: "text.primary", borderColor: "divider", "&:hover": { borderColor: "text.primary", bgcolor: "transparent" } }),
      }}
    >
      {cta.label}
    </Button>
  );
}

/** The closing band every public page ends on. */
export function CtaBand({ id, title, body, primary, secondary, secondaryExternal = false }: { id: string; title: string; body: string; primary: Cta; secondary?: Cta; secondaryExternal?: boolean }): JSX.Element {
  return (
    <Section id={id}>
      <Box sx={{ borderRadius: 4, p: { xs: 4, md: 7 }, textAlign: "center", color: "accent.contrastText", backgroundImage: (t) => `linear-gradient(135deg, ${t.palette.accent[700]}, ${t.palette.accent[800]})` }}>
        <Typography id={`${id}-heading`} component="h2" sx={{ fontSize: { xs: "1.7rem", md: "2.2rem" }, fontWeight: 700, letterSpacing: "-0.02em" }}>{title}</Typography>
        <Typography sx={{ mt: 1.5, mb: 4, opacity: 0.85, fontSize: 17 }}>{body}</Typography>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
          <Button component={RouterLink} to={primary.href} size="large" variant="contained" disableElevation sx={{ borderRadius: 999, px: 3.5, fontWeight: 700, textTransform: "none", bgcolor: "common.white", color: "accent.800", "&:hover": { bgcolor: "common.white" } }}>{primary.label}</Button>
          {secondary ? (
            <Button {...(secondaryExternal ? { href: secondary.href } : { component: RouterLink, to: secondary.href })} size="large" variant="outlined" sx={{ borderRadius: 999, px: 3.5, fontWeight: 700, textTransform: "none", color: "inherit", borderColor: (t) => alpha(t.palette.common.white, 0.5), "&:hover": { borderColor: "inherit" } }}>{secondary.label}</Button>
          ) : null}
        </Box>
      </Box>
    </Section>
  );
}

/** The small hero the Program page opens with (the landing page has its
 * own, larger one).
 *
 * A plain `<div>`, not a `<header>`: spec §10 allows one `<header>` per
 * page and `TopBar` already spends it. A `<header>` scoped to `<main>`
 * loses its banner role in a real browser, so nesting one here would be
 * legal — but "one per page" is the rule that is actually checkable, and
 * the h1 and lede need no element of their own to be read correctly.
 *
 * The top padding is larger than the space the h1 needs on its own: with
 * the eyebrow gone the heading sat too close under the fixed top bar, so
 * the label's height is returned as padding instead of left as a gap. */
export function PageHero({ title, lede }: { title: string; lede: string }): JSX.Element {
  return (
    <Box component="div" sx={{ pt: { xs: 8, md: 13 }, pb: { xs: 2, md: 4 } }}>
      <Container maxWidth="lg">
        <Box sx={{ maxWidth: 760 }}>
          <Typography component="h1" sx={{ fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1.05, fontSize: "clamp(2.2rem, 5vw, 3.6rem)" }}>{title}</Typography>
          <Typography sx={{ mt: 3, fontSize: 19, lineHeight: 1.6, color: "text.secondary" }}>{lede}</Typography>
        </Box>
      </Container>
    </Box>
  );
}
